import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_KEY = 'tai.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly mediaQuery =
    typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  readonly preference = signal<ThemePreference>(this.readPreference());

  constructor() {
    this.apply(this.preference());
    this.mediaQuery?.addEventListener('change', () => {
      if (this.preference() === 'system') this.apply('system');
    });
  }

  setTheme(preference: ThemePreference): void {
    this.preference.set(preference);
    localStorage.setItem(THEME_KEY, preference);
    this.apply(preference);
  }

  set(preference: ThemePreference): void { this.setTheme(preference); }

  private readPreference(): ThemePreference {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  }

  private apply(preference: ThemePreference): void {
    const isDark = preference === 'dark' || (preference === 'system' && !!this.mediaQuery?.matches);
    this.document.documentElement.classList.toggle('dark', isDark);
    this.document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }
}
